"use client";

import { zodResolver } from "@hookform/resolvers/zod";
import { IconAdjustments, IconLoader2 } from "@tabler/icons-react";
import { usePathname, useRouter } from "next/navigation";
import { useTranslations } from "next-intl";
import { useEffect } from "react";
import {
  type FieldValues,
  type UseFormReturn,
  useForm,
  useWatch,
} from "react-hook-form";
import { z } from "zod";
import { BottomSheetDrawer } from "@/components/bottom-sheet-drawer";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Form } from "@/components/ui/form";
import { SheetClose } from "@/components/ui/sheet";
import { getDietPriceRangePresets } from "@/features/diets/constants/diet-price-range-presets";
import { serializeTagsClient } from "@/features/diets/helpers/serialize-tags";
import type { DietSearchResultsType } from "@/features/diets/types/diet-search-resilts.type";
import { FilterBlock } from "@/features/filters/components/filter-block";
import { FilterCalories } from "@/features/filters/components/filter-calories";
import { FilterMeals } from "@/features/filters/components/filter-meals";
import { FilterMulti } from "@/features/filters/components/filter-multi/filter-multi";
import { FilterRangeForm } from "@/features/filters/components/filter-range/filter-range-form";
import { FilterTags } from "@/features/filters/components/filter-tags";
import type { FiltersDietsType } from "@/features/filters/types/filters-diets.type";
import type { FiltersSetup } from "@/features/filters/types/filters-setup.type";
import { useDietsResults } from "@/features/multidiet/api/hooks/use-get-diets-results";
import { defaultLocale } from "@/i18n/defaults";
import {
  parseCaloricityParams,
  serializeFormValues,
} from "@/lib/helpers/parsers";
import { objectToQueryParams } from "@/lib/utilities";
import { sortingData } from "../data/sorting";

export const DietsMobileFiltersForm = ({
  filtersSetup,
  parsedParams,
}: {
  filtersSetup: FiltersSetup;
  parsedParams: FiltersDietsType;
}) => {
  const t = useTranslations();
  const serializedTags = serializeTagsClient(filtersSetup?.tags);
  const priceRangePresets = getDietPriceRangePresets({
    labels: {
      below30: t("diets.filters.price.presets.below30"),
      between30And60: t("diets.filters.price.presets.between30And60"),
      between60And80: t("diets.filters.price.presets.between60And80"),
      above80: t("diets.filters.price.presets.above80"),
    },
    maxValue: filtersSetup.prices.defaultTo,
    locale: defaultLocale,
  });

  const form = useForm<FieldValues>({
    resolver: zodResolver(
      z.object({
        sortOption: z.string().optional(),
        tagsSlugs: z.array(z.string()).optional(),
        caloricity: z.string().optional(),
        preset: z.string().optional(),
        "promoPrice[gte]": z.string().optional(),
        "promoPrice[lte]": z.string().optional(),
      }),
    ),
    defaultValues: {
      sortOption: parsedParams.sortOption || "default",
      tagsSlugs: parsedParams.tagsSlugs?.split(",") || [],
      caloricity:
        filtersSetup.calories.find(
          (calorie) => calorie.label === parsedParams.caloricity,
        )?.label || "",
      preset: parsedParams.preset || "",
      "promoPrice[gte]": parsedParams["promoPrice[gte]"] || "",
      "promoPrice[lte]": parsedParams["promoPrice[lte]"] || "",
    },
  });

  const tagsSlugs = useWatch({
    control: form.control,
    name: "tagsSlugs",
  });
  const caloricity = useWatch({
    control: form.control,
    name: "caloricity",
  }) as FiltersDietsType["caloricity"];
  const preset = useWatch({
    control: form.control,
    name: "preset",
  });
  const promoPriceGte = useWatch({
    control: form.control,
    name: "promoPrice[gte]",
  });
  const promoPriceLte = useWatch({
    control: form.control,
    name: "promoPrice[lte]",
  });

  const {
    data: dietsResults,
    isPending,
    refetch,
  } = useDietsResults({
    filters: {
      ...serializeFormValues({
        tagsSlugs,
        preset,
      }),
      ...parseCaloricityParams({ caloricity: caloricity }, filtersSetup),
      "promoPrice[gte]": promoPriceGte.toString(),
      "promoPrice[lte]": promoPriceLte.toString(),
    },
  });

  useEffect(() => {
    if (!form.formState.isDirty) {
      return;
    }

    refetch();
  }, [
    form.formState.isDirty,
    refetch,
    tagsSlugs,
    caloricity,
    preset,
    promoPriceGte,
    promoPriceLte,
  ]);

  useEffect(() => {
    form.reset({
      sortOption: parsedParams.sortOption || "default",
      tagsSlugs: parsedParams.tagsSlugs?.split(",") || [],
      caloricity:
        filtersSetup.calories.find(
          (calorie) => calorie.label === parsedParams.caloricity,
        )?.label || "",
      preset: parsedParams.preset || "",
      "promoPrice[gte]": parsedParams["promoPrice[gte]"] || "",
      "promoPrice[lte]": parsedParams["promoPrice[lte]"] || "",
    });
  }, [parsedParams]);
  return (
    <Form {...form}>
      <BottomSheetDrawer
        className="z-99999999"
        content={
          <div className="p-4">
            <div className="rounded-lg bg-brand-grey1 p-4">
              {filtersSetup && serializedTags && (
                <FilterTags
                  filtersSetup={filtersSetup}
                  serializedTags={serializedTags}
                  collapsible={false}
                  asForm={true}
                  form={form}
                />
              )}
            </div>
          </div>
        }
        title={t("filters.labels.diet_type")}
        trigger={
          <Button variant="outline">
            {t("filters.labels.diet_type")}{" "}
            {tagsSlugs?.length > 0 ? `(${tagsSlugs?.length})` : ""}
          </Button>
        }
        footer={
          <Footer
            form={form}
            isPending={isPending}
            dietsResults={dietsResults}
            fieldName="tagsSlugs"
          />
        }
      />

      <BottomSheetDrawer
        className="z-99999999"
        content={
          <div className="p-4">
            <div className="rounded-lg bg-brand-grey1 p-4">
              <FilterCalories
                filtersSetup={filtersSetup}
                collapsible={false}
                asForm={true}
                form={form}
              />
            </div>
          </div>
        }
        title={t("filters.labels.caloricity")}
        trigger={
          <Button variant="outline">
            {t("filters.labels.caloricity")} {caloricity ? `(1)` : ""}
          </Button>
        }
        footer={
          <Footer
            form={form}
            isPending={isPending}
            dietsResults={dietsResults}
            fieldName="caloricity"
          />
        }
      />

      <BottomSheetDrawer
        className="z-99999999"
        content={
          <div className="p-4">
            <div className="rounded-lg bg-brand-grey1 p-4">
              <FilterMeals
                filtersSetup={filtersSetup}
                collapsible={false}
                asForm={true}
                form={form}
              />
            </div>
          </div>
        }
        title={t("filters.labels.meals")}
        trigger={
          <Button variant="outline">
            {t("filters.labels.meals")} {preset ? `(1)` : ""}
          </Button>
        }
        footer={
          <Footer
            form={form}
            isPending={isPending}
            dietsResults={dietsResults}
            fieldName="preset"
          />
        }
      />

      <BottomSheetDrawer
        className="z-99999999"
        content={
          <div className="p-4">
            <div className="rounded-lg bg-brand-grey1 p-4">
              <FilterRangeForm
                form={form}
                labelEnd={t("diets.filters.price.to")}
                labelStart={t("diets.filters.price.from")}
                maxValue={filtersSetup.prices.defaultTo}
                minValue={filtersSetup.prices.defaultFrom}
                paramEnd="promoPrice[lte]"
                paramStart="promoPrice[gte]"
                rangePresets={priceRangePresets}
                unit={t(`common.currencies.${defaultLocale}`)}
              />
            </div>
          </div>
        }
        title={t("filters.labels.price")}
        trigger={
          <Button variant="outline">
            {t("filters.labels.price")}{" "}
            {promoPriceGte || promoPriceLte ? `(1)` : ""}
          </Button>
        }
        footer={
          <Footer
            form={form}
            isPending={isPending}
            dietsResults={dietsResults}
            fieldName={["promoPrice[gte]", "promoPrice[lte]"]}
          />
        }
      />

      <BottomSheetDrawer
        className="z-99999999"
        content={
          <div className="p-4">
            <Accordion
              className="flex w-full flex-col gap-4"
              defaultValue={["tagsSlugs"]}
              type="multiple"
            >
              <FilterBlock label={t("filters.sort")} name="sortOption">
                <FilterMulti
                  layout="horizontalScroll"
                  collapsible={false}
                  multiple={false}
                  asForm={true}
                  form={form}
                  filter={{
                    label: "",
                    name: "sortOption",
                    total: sortingData.length,
                    value: [],
                    options: sortingData.map((item) => ({
                      label: (
                        <span className="flex items-center gap-2">
                          {t(`diets.filters.sort.${item.label}`)}
                        </span>
                      ),
                      value: item.value,
                    })),
                  }}
                  paramName="sortOption"
                  type="button"
                />
              </FilterBlock>
              {serializedTags && (
                <FilterBlock
                  filterCounter
                  label={t("filters.labels.diet_type")}
                  name="tagsSlugs"
                >
                  <FilterTags
                    filtersSetup={filtersSetup}
                    serializedTags={serializedTags}
                    asForm={true}
                    form={form}
                  />
                </FilterBlock>
              )}

              <FilterBlock
                filterCounter
                label={t("filters.labels.caloricity")}
                name="caloricity"
              >
                <FilterCalories
                  filtersSetup={filtersSetup}
                  asForm={true}
                  form={form}
                />
              </FilterBlock>

              <FilterBlock
                filterCounter
                label={t("filters.labels.meals")}
                name="preset"
              >
                <FilterMeals
                  filtersSetup={filtersSetup}
                  asForm={true}
                  form={form}
                />
              </FilterBlock>
              <FilterBlock
                filterCounter
                label={t("diets.filters.price.label")}
                name="price"
              >
                <FilterRangeForm
                  form={form}
                  labelEnd={t("diets.filters.price.to")}
                  labelStart={t("diets.filters.price.from")}
                  maxValue={filtersSetup.prices.defaultTo}
                  minValue={filtersSetup.prices.defaultFrom}
                  paramEnd="promoPrice[lte]"
                  paramStart="promoPrice[gte]"
                  rangePresets={priceRangePresets}
                  unit={t(`common.currencies.${defaultLocale}`)}
                />
              </FilterBlock>
            </Accordion>
          </div>
        }
        title={t("filters.filter")}
        trigger={
          <Button variant="outline">
            {t("filters.labels.all_filters")}{" "}
            <IconAdjustments className="ml-2" />
          </Button>
        }
        footer={
          <Footer
            form={form}
            isPending={isPending}
            dietsResults={dietsResults}
            fieldName="all"
          />
        }
      />
    </Form>
  );
};

const Footer = ({
  form,
  isPending,
  dietsResults,
  fieldName,
}: {
  form: UseFormReturn<FieldValues>;
  isPending: boolean;
  dietsResults: DietSearchResultsType | undefined;
  fieldName: string | string[];
}) => {
  const { replace } = useRouter();
  const pathname = usePathname();
  const t = useTranslations("filters");

  const getResetValue = (name: string) => {
    if (name === "sortOption") {
      return "default";
    }

    if (name === "tagsSlugs") {
      return [];
    }

    return "";
  };

  const handleReset = () => {
    if (fieldName === "all") {
      form.reset({
        sortOption: "default",
        tagsSlugs: [],
        caloricity: "",
        preset: "",
        "promoPrice[gte]": "",
        "promoPrice[lte]": "",
      });
    } else if (Array.isArray(fieldName)) {
      for (const name of fieldName) {
        form.setValue(name, getResetValue(name), {
          shouldDirty: true,
          shouldTouch: true,
        });
      }
    } else {
      form.setValue(fieldName, getResetValue(fieldName), {
        shouldDirty: true,
        shouldTouch: true,
      });
    }
    replace(pathname, { scroll: false });
  };

  const handleApply = () => {
    form.handleSubmit(onSubmit)();
  };

  const onSubmit = (data: FieldValues) => {
    const values = serializeFormValues({
      ...data,
      "promoPrice[gte]": form.getValues("promoPrice[gte]"),
      "promoPrice[lte]": form.getValues("promoPrice[lte]"),
    });

    const clearedValues = Object.fromEntries(
      Object.entries(values).filter(
        ([key, value]) => !(key === "sortOption" && value === "default"),
      ),
    );

    replace(
      `${pathname}?${objectToQueryParams(clearedValues, new URLSearchParams()).toString()}`,
    );
  };

  const selectedTags = form.getValues("tagsSlugs");
  const hasPrice = Boolean(
    form.getValues("promoPrice[gte]") || form.getValues("promoPrice[lte]"),
  );
  const countNotEmptyFields =
    (Array.isArray(selectedTags) && selectedTags.length > 0 ? 1 : 0) +
    (form.getValues("caloricity") ? 1 : 0) +
    (form.getValues("preset") ? 1 : 0) +
    (hasPrice ? 1 : 0);

  return (
    <div className="flex gap-2">
      <Button
        type="button"
        variant="outline"
        className="flex-1"
        onClick={handleReset}
      >
        {fieldName === "all"
          ? t("reset_all") +
            (countNotEmptyFields > 0 ? ` (${countNotEmptyFields})` : "")
          : t("reset")}
      </Button>
      <SheetClose asChild>
        <Button
          variant="secondary"
          disabled={isPending && !form.formState.isDirty}
          className="flex-1"
          onClick={handleApply}
        >
          {isPending && form.formState.isDirty ? (
            <IconLoader2 className="ml-2 animate-spin" />
          ) : (
            t("show", { count: dietsResults?.["hydra:totalItems"] || 0 })
          )}
        </Button>
      </SheetClose>
    </div>
  );
};
